import { useState, useRef, useEffect, useCallback, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { Autocomplete, TextField } from "@mui/material";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";
import GavelIcon from '@mui/icons-material/Gavel';
import ViewInArIcon from '@mui/icons-material/ViewInAr';

import { useDispatch } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";
import { isEmpty, isEqual, isNull } from "lodash";

import prodOrderService from "@plannel/services/data/prod-order-service";
import mpDemandService from "@plannel/services/mp/mp-demand-service";
import userService from "@plannel/services/system/user-service";
import MpDemandAddModal from "@plannel/pages/master-plan/mp-modal/MpDemandAddModal";
import MpDemandConstraintModal, { resolveOrderType } from "@plannel/pages/master-plan/mp-modal/MpDemandConstraintModal";
import mpDemandConstraintService from "@plannel/services/mp/mp-demand-constraint-service";

import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton, FilterButton, AddButton, RemoveButton, SettingsButton } from "@plannel/components/ActionIconButton";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ResponseUtil from "@plannel/pages/utils/response-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import dateUtils from "@plannel/utils/date-util";
import { green, grey } from "@mui/material/colors";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

const PLANNING_RANGE = "PLANNING_RANGE";
const BUCKET_UNIT = "BUCKET_UNIT";
const { snackBarMessageColor } = ColorUtil;
const { versionStatusCode, mpDemandType, buckets, roles, orderType } = CodeUtil;
const { RELEASED } = versionStatusCode;
const { FORECAST, REPLENISHMENT, PRODORDER } = mpDemandType;
const { httpStatusCode, responseCode } = ResponseUtil;

const CURRENT_DATE = new ZDate();
const START_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
const END_MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 10, CURRENT_DATE.getMonth() + 1, 0);
const INIT_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), CURRENT_DATE.getDate() + 120);
const PeriodFilter = ({ period, setPeriod }) => {
  const { t } = useTranslation();

  const onChange = (e, type) => {
    if (type) {
      if (type === "start") {
        e.setHours(0);
        e.setMinutes(0);
        e.setSeconds(0);
      } else if (type === "end") {
        e.setHours(23);
        e.setMinutes(59);
        e.setSeconds(59);
      }

      setPeriod({ ...period, [type]: e });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label={t("periodStart")}
        views={["year", "month", "day"]}
        format={"yyyy-MM-dd"}
        openTo="day"
        value={period.start}
        maxDate={period.end ? period.end : END_MAX_DATE}
        minDate={START_MIN_DATE}
        onAccept={e => onChange(e, "start")}
        // onClose={onChange}
        slotProps={{ textField: { InputProps: { color: "primary", size: "small", sx: { mr: 1.5, width: 200 } } } }}
      />
      <DatePicker
        label={t("periodEnd")}
        views={["year", "month", "day"]}
        format={"yyyy-MM-dd"}
        openTo="day"
        value={period.end}
        maxDate={END_MAX_DATE}
        minDate={period.start ? period.start : START_MIN_DATE}
        onAccept={e => onChange(e, "end")}
        // onClose={onChange}
        slotProps={{ textField: { InputProps: { color: "primary", size: "small", sx: { mr: 1.5, width: 200 } } } }}
      />
    </LocalizationProvider>
  );
};

const MpDemand = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const user = JSON.parse(localStorage.getItem("user"));
  const [userRoles, setUserRoles] = useState(reduxViewState?.userRoles ?? []);
  const planningRangeValue = useRef(120);
  const bucketUnitValue = useRef(buckets.DAY);

  /* Search Filter */
  const [mpDemandVersionFilter, setMpDemandVersionFilter] = useState(reduxViewState?.mpDemandVersion ? reduxViewState.mpDemandVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null });
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [demandType, setDemandType] = useState(reduxViewState?.demandType ?? null);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ?? null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");
  const [period, setPeriod] = useState({ 
    start: new ZDate(CURRENT_DATE),
    end: new ZDate(INIT_END_DATE)
  });

  /* Version Filter List */
  const [mpDemandVersionList, setMpDemandVersionList] = useState([]);
  const [demandVersionModule, setDemandVersionModule] = useState("");

  /* Grid Data */
  const [resultData, setResultData] = useState([]);

  const currentPage = useRef(reduxViewState ? reduxViewState.currentPage : 0);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [demandTypes, setDemandTypes] = useState([]);
  const [mpOnly, setMpOnly] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedDemandRow, setSelectedDemandRow] = useState(null);
  const [constraintModalOpen, setConstraintModalOpen] = useState(false);
  // 선택한 수요에 제약(재고/재공/공정/자원 할당)이 저장되어 있는지 — 설정 버튼 색으로 표시한다
  const [hasConstraint, setHasConstraint] = useState(false);

  /* Rendering Timing Adjust */
  const [gridLoading, setGridLoading] = useState(false);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [periodLoading, setPeriodLoading] = useState(false);

  const defaultGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({ title, viewName });
    defaultSettings.defaultColDef.filter = true;
    return defaultSettings;
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const valueFormatter = params => {
    if (params.data?.demandType) {
      return GridUtils.gridValueL10N(params.data.demandType);
    } else if (params?.column?.colDef?.field === "demandType") {
      return GridUtils.gridValueL10N(params.value);
    }

    return "";
  }

  /* prod_order 에서 온 Demand 에만 order type 표시 */
  const orderTypeFormatter = params => {
    const value = params.data?.orderType ?? params.value;
    return orderType?.[value] ? GridUtils.gridValueL10N(orderType[value]) : "";
  }

  const setToolTip = (version) => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE: startDate, BUCKET_UNIT: unit, PLANNING_RANGE: range, START_DAY: startDay } = version?.planConfigObj;
      let format = "yyyy-MM-dd";
      if (isEqual(unit, buckets.MONTH)) {
        format = "yyyy-MM";
      }
      return `${dateUtils.formatDate(startDate, format)} ~
        ${dateUtils.formatDate(dateUtils.calculateRangeDate(startDate, unit, startDay, range, true), format)}`
    }
    return null;
  }

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columnDefs = [
    { headerName: "locCd", field: "locCd", type: ["nonEditableColumn"], width: 150, cellClass: 'stringType', filterType: 'string' },
    { headerName: "locName", field: "locName", type: ["nonEditableColumn"], width: 150, filterType: 'string' },
    { headerName: "reqLocCd", field: "reqLocCd", type: ["nonEditableColumn"], width: 150, cellClass: 'stringType', filterType: 'string' },
    { headerName: "reqLocName", field: "reqLocName", type: ["nonEditableColumn"], width: 150, filterType: 'string' },
    { headerName: "customerCd", field: "customerCd", type: ["nonEditableColumn"], cellClass: "stringType", width: 150, filterType: 'string' },
    { headerName: "customerName", field: "customerName", type: ["nonEditableColumn"], width: 150, filterType: 'string' },
    { headerName: "itemCd", field: "itemCd", cellClass: "stringType", type: ["nonEditableColumn"], width: 150, filterType: 'string' },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 150, filterType: 'string' },
    { headerName: "dueDate", field: "dueDate", type: ["nonEditableColumn"], width: 160, filterType: 'date' },
    {
      headerName: "demandType", field: "demandType", type: ["nonEditableColumn"], width: 150,
      valueFormatter, filterValueGetter: valueFormatter, filterType: 'select', filterOption: Object.values(mpDemandType)
    },
    {
      headerName: "orderType", field: "orderType", type: ["nonEditableColumn"], width: 150,
      /* filterOption 값은 소문자 코드로 준다 - 그 값이 언어팩 키이면서(라벨 번역) 서버 비교가 UPPER 라 DB 값과도 맞는다 */
      valueFormatter: orderTypeFormatter, filterValueGetter: orderTypeFormatter, filterType: 'string',
      filterOption: Object.keys(orderType).map(key => key.toLowerCase())
    },
    { headerName: "orderNum", field: "orderNum", type: ["nonEditableColumn"], width: 150, filterType: 'string' },
    { headerName: "demandQty", field: "demandQty", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, filterType: 'number' },
    { headerName: "uomCd", field: "uomCd", type: ["nonEditableColumn"], width: 100, filterType: 'string' },
    { headerName: "demandAmt", field: "demandAmt", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, filterType: 'number' },
    { headerName: "currencyCd", field: "currencyCd", type: ["nonEditableColumn"], width: 150, sortable: false, filterType: 'string' },
    { headerName: "stdSalesPrice", field: "stdUnitPrice", type: ["decimalPointColumn", "nonEditableColumn", "rightAligned"], width: 160, filterType: 'number' },
    { headerName: "salesUnitCost", field: "salesUnitCost", type: ["decimalPointColumn", "nonEditableColumn", "rightAligned"], width: 150, filterType: 'number' },
    { headerName: "invUnitCost", field: "invUnitCost", type: ["decimalPointColumn", "nonEditableColumn", "rightAligned"], width: 160, filterType: 'number' },
    { headerName: "itemPriority", field: "itemPriority", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, filterType: 'number' },
    { headerName: "customerPriority", field: "customerPriority", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, filterType: 'number' },    
  ];

  const periodCalculate = (version) => {
    let startDate = new ZDate(CURRENT_DATE);
    let endDate = dateUtils.calculateRangeDate(startDate, bucketUnitValue.current, null, planningRangeValue.current, true);

    if (!isEmpty(version)) {
      const selectedVersionPlanConfig = version?.planConfigObj;

      if (selectedVersionPlanConfig) {
        const { PLAN_START_DATE: planStartDate, BUCKET_UNIT: unit, PLANNING_RANGE: range, START_DAY: startDay } = selectedVersionPlanConfig;

        startDate = new ZDate(planStartDate);
        endDate = dateUtils.calculateRangeDate(startDate, unit, startDay, range, true);
      }
    }

    setPeriod({
      start: startDate,
      end: endDate
    });

    setPeriodLoading(true);
  }

  const onChangeVersionHandler = (selectedVersion) => {
    setMpDemandVersionFilter(selectedVersion);
    periodCalculate(selectedVersion);
  }

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (!isEmpty(mpDemandVersionFilter)) {
      searchFilters.versionId = mpDemandVersionFilter.id;
      searchFilters.moduleCd = mpDemandVersionFilter.moduleCd;
    }

    if (period) {
      searchFilters.startDate = period.start
      searchFilters.endDate = period.end
    }

    if (filterValue?.locations) {
      searchFilters.locationCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch?.current) {
      searchFilters.customer = customerSearch.current;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (demandType) {
      searchFilters.demandType = demandType;
    }

    params.searchFilters = searchFilters;

    if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    params.pagination = false;

    return params;
  };

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    if (gridLoading) {
      initPageToRetrieve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const iconColor = planVersion => {
      if (planVersion) {
        if (isEqual(planVersion.statusCd, RELEASED)) {
          return grey[900];
        }
      }
  
      return green[600];
    }

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: user.username })
      .then(
        (response) => {
          const userAccess = response.data;
          const userRolesData = userAccess?.roles || [];
          setUserRoles(userAccess?.roles);

          if (userRolesData.includes(roles.APP_RP)) {
            setDemandVersionModule("rp");
          } else if (userRolesData.includes(roles.APP_DP)) {
            setDemandVersionModule("dp");
          }
        },
      )
      .catch(e => { debug(e); });
  };

  const retrieveMpDemandVerisons = (mpOnly) => {
    mpDemandService.getMpDemandVersions()
    .then(response => {
      if (response.data) {
        const { data } = response;

        /* 버전 조회시 Mp Settings 데이터 셋팅 */
        planningRangeValue.current = data?.[PLANNING_RANGE] ? Number(data[PLANNING_RANGE]) : 120;
        bucketUnitValue.current = data?.[BUCKET_UNIT] || buckets.DAY;

        if (mpOnly) {
          periodCalculate(null);

          setMpDemandVersionFilter(null);
          setMpDemandVersionList([]);
          return;
        }

        if (isEmpty(data?.results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });

          setMpDemandVersionFilter(null);
          setMpDemandVersionList([]);
        } else {
          const mpDemandVersion = reduxViewState?.mpDemandVersion || data.results[0];
          
          periodCalculate(mpDemandVersion);

          setMpDemandVersionFilter(mpDemandVersion);
          setMpDemandVersionList(data.results);
        }
      }
    })
    .catch(e => {
      const { data } = e.response;
      if (data?.message) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t(data.message) });
      }
      debug(e);
    })

  }

  const retrieveMpDemand = () => {
    gridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();

    mpDemandService.get(params)
      .then(response => {
        if (response.data) {
          const { results } = response.data;
          setResultData(results);

          if (isEmpty(results)) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          }
        }
      })
      .catch(e => {
        const { data } = e.response;
        if (data?.message) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t(data.message) });
        }
        debug(e);
      })
      .then(() => {
        const reduxFilterValue = { mpDemandVersion: mpDemandVersionFilter, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, demandType, userRoles };
        const reduxState = reduxUtil.createObjectState(viewName, reduxFilterValue);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false)
      });
  };

  const demandTypeAndModuleHandler = (userRoles) => {
    const demandTypeArray = [];
    
    let isMpModuleOnly = true;
    if (userRoles.includes(roles.APP_RP)) {
      demandTypeArray.push(REPLENISHMENT);

      isMpModuleOnly = false;
    } else if (userRoles.includes(roles.APP_DP)) {
      demandTypeArray.push(FORECAST);

      isMpModuleOnly = false;
    }


    demandTypeArray.push(PRODORDER);

    setDemandTypes(demandTypeArray);
    setMpOnly(isMpModuleOnly);
    setModuleLoading(true);
  }

  const onGridReady = params => {
    setGridLoading(true);

    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const onSelectionChanged = useCallback((params) => {
    const selectedRows = params.api.getSelectedRows();
    setSelectedDemandRow(selectedRows[0] ?? null);
  }, []);

  const onPaginationChanged = useCallback((params) => {
    if (gridRef.current.api) {
      const totalPages = params.api.paginationGetTotalPages();
      if (params.newPage && totalPages > 0) {
        const page = params.api.paginationGetCurrentPage();
        currentPage.current = page;

        const reduxState = reduxUtil.createObjectState(viewName, { currentPage: page });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      }

      if (params.newData && totalPages > 0) {
        gridRef.current.api.paginationGoToPage(currentPage.current);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initPageToRetrieve = () => {
    if (currentPage.current > 0) {
      currentPage.current = 0;
      gridRef.current.api.paginationGoToPage(currentPage.current);
    }

    retrieveMpDemand();
  }

  // STEP1 Effect
  useEffect(() => {
    if (isEmpty(userRoles)) {
      retrieveUserRoles();
    } else {
      demandTypeAndModuleHandler(userRoles); // 사용하는 Module 및 Demand Type useState 설정 
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles])

  // STEP2 Effect
  useEffect(() => {
    if (!isEmpty(demandTypes)) {
      retrieveMpDemandVerisons(mpOnly);
    } 

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandTypes])

  // STEP3 Effect
  useEffect(() => {
    if (userRoles && gridLoading && (!isEmpty(mpDemandVersionFilter) || mpOnly) && period && moduleLoading && periodLoading) {
      initPageToRetrieve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpDemandVersionFilter, filterValue, demandType, period, moduleLoading, periodLoading]);

  /*
   * 선택한 수요 1건의 제약 설정 여부 조회 — 제약 설정 팝업이 복원에 쓰는 것과 같은 키/API 를 쓴다.
   * 팝업이 열려 있는 동안은 조회하지 않고, 닫힐 때(저장·취소) 다시 읽어 색을 갱신한다.
   */
  const constraintPlanVerId = mpDemandVersionFilter?.id;
  useEffect(() => {
    if (constraintModalOpen) return undefined;

    const dueDate = dateUtils.formatDate(selectedDemandRow?.dueDate, "yyyy-MM-dd");
    // 수요 버전이 없으면(MP 단독) 저장 자체가 불가하므로 항상 미설정으로 본다
    if (!constraintPlanVerId || !selectedDemandRow?.locCd || !selectedDemandRow?.itemCd || !dueDate) {
      setHasConstraint(false);
      return undefined;
    }

    let canceled = false;
    mpDemandConstraintService.getAllocation({
      parPlanVerId: constraintPlanVerId,
      locCd: selectedDemandRow.locCd,
      itemCd: selectedDemandRow.itemCd,
      dueDate,
      orderType: resolveOrderType(selectedDemandRow),
      reqLocCd: selectedDemandRow.reqLocCd ?? null,
      customerCd: selectedDemandRow.customerCd ?? null,
    }).then((res) => {
      if (canceled) return;
      // 저장 이력이 없으면 204(No Content) — 네 탭이 모두 비어 있으면 미설정으로 본다(팝업 복원 판정과 동일)
      const results = res?.data?.results ?? {};
      setHasConstraint(["inventory", "wip", "route", "resource"].some(key => !isEmpty(results[key])));
    }).catch((e) => {
      // 조회 실패 시에는 색을 올리지 않는다(설정되어 있다고 오인하게 만들지 않기 위함)
      if (!canceled) setHasConstraint(false);
      debug(e);
    });

    return () => { canceled = true; };
  }, [selectedDemandRow, constraintPlanVerId, constraintModalOpen]);

const saveProdOrder = (data) => {
    if (data.length > 0) {
      prodOrderService.upsert(data)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const status = e?.response?.status;
          const errorDatas = e?.response?.data;
          const { message } = errorDatas;

          if (status === httpStatusCode.BadRequest) {
            for (const errorCode of Object.keys(errorDatas)) {
              if (errorCode === responseCode.DUPLICATE_ORDER_NUM) {
                setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_DuplicateOrderNum", { orderNums: errorDatas[errorCode] }) });
              } else if (errorCode === responseCode.NOT_FOUND_ITEM) {
                setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotExistItemCodes", { itemCodes: errorDatas[errorCode] }) });
              } else if (errorCode === responseCode.INVALID_ITEM_TYPE) {
                setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ItemTypeCheck", { itemCodes: errorDatas[errorCode] }) });
              } else {
                setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
              }
              break;
            }
          } else if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveMpDemand();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  /*
   * 삭제 전 선택 검증 — 원본 데이터를 지울 수 있는 것은 생산 주문(PRODORDER)에서 온 수요뿐이다.
   * 수요계획/보충계획 수요는 각 모듈의 계획 결과이므로 이 화면에서 지우지 않는다.
   *
   * 생산 주문 행에만 체크박스를 노출하지만 헤더 전체선택/행 클릭(Ctrl·Shift)으로 다른 타입이 섞일 수 있다.
   * 이때 생산 주문만 골라 조용히 지우면 사용자가 의도한 삭제 범위와 달라지므로, 하나라도 섞이면 아예 중단하고
   * 몇 건이 문제인지 알려준다. 확인 팝업을 띄우기 전에 검사해서 "확인 → 거절" 이 되지 않게 한다.
   */
  const openDeleteDialog = () => {
    const selectedRows = gridRef.current?.api?.getSelectedRows() ?? [];

    if (isEmpty(selectedRows)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_DeleteNoData") });
      return;
    }

    const notProdOrderCount = selectedRows.filter(row => !isEqual(row.demandType, PRODORDER)).length;
    if (notProdOrderCount > 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.validation, content: t("MSG_OnlyProdOrderDeletable", { N: notProdOrderCount }) });
      return;
    }

    setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") });
  };

  const removeProdOrderDemand = () => {
    // openDeleteDialog 에서 전 행이 생산 주문임을 이미 확인했다 — prodOrderId 유무만 방어적으로 걸러낸다.
    const prodOrderIds = gridRef.current?.api?.getSelectedRows()
      .filter(row => row.prodOrderId)
      .map(row => row.prodOrderId) ?? [];

    if (isEmpty(prodOrderIds)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_DeleteNoData") });
      return;
    }

    prodOrderService.remove(prodOrderIds)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
      })
      .catch(e => {
        const message = e?.response?.data?.message;
        if (message) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(message) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        }
        debug(e);
      })
      .then(() => {
        retrieveMpDemand();
      });
  };

  const handleDialog = (action) => {
    if (isEqual(action, "DELETE")) {
      removeProdOrderDemand();
    }
  };

  return (
    <>
      <FilterContainer>
        <VersionAutocomplete
          options={mpDemandVersionList}
          value={!isNull(mpDemandVersionFilter) ? mpDemandVersionFilter : null}
          onChange={(_, newValue) => onChangeVersionHandler(newValue)}
          label={`${t(demandVersionModule)} ${t("version")}`}
          getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
          getIconColor={iconColor}
          getTooltip={setToolTip}
          disabled={mpOnly}
          inputIconSx={{ mr: 1 }}
        />
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <CustomerAutocomplete
          retrieve={initPageToRetrieve}
          searchValue={customerSearch}
          extraCodesRef={extraCustomerCodesRef}
        />
        <MPItemAutocomplete
          retrieve={initPageToRetrieve}
          searchValue={itemSearch}
          extraCodesRef={extraItemCodesRef}
        />
        <Box id="mp-demand-auto">
          <Autocomplete
            options={demandTypes}
            getOptionLabel={option => t(option)}
            value={demandType}
            onChange={(_, newValue) => setDemandType(newValue)}
            isOptionEqualToValue={(option, value) => option === value}
            size="small"
            sx={{ minWidth: 230, width: 240, mr: 1 }}
            renderInput={(params) => (
              <TextField {...params} label={t("demandType")} />
            )}
          />
        </Box>
        <PeriodFilter period={period} setPeriod={setPeriod} />
        <Box sx={{ ml: "auto" }}>
          <AddButton onClick={() => setAddModalOpen(true)} />
          {/* 미설정은 "black"(SettingsButton 기본값) — MUI 의 "default" 는 action.active(회색 54%)라
              같은 줄의 다른 아이콘(전부 black)보다 흐려 보였다. 제약이 걸린 행은 info 로 구분한다. */}
          <RemoveButton onClick={openDeleteDialog} />
          <SettingsButton
            label="mpDemandConstraintSetting"
            color={hasConstraint ? "info" : "black"}
            disabled={!selectedDemandRow}
            onClick={() => setConstraintModalOpen(true)}
          />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={() => setOpenFilter(!openFilter)} />
          <DownloadButton label="exportAll" onClick={() => gridRef.current.api.exportDataAsExcel()} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 63px)", display: 'flex', flexDirection: 'row' }}>
        <Box sx={{ flex: 1, height: '100%' }}>
          <AgGridReact
            ref={gridRef}
            rowData={resultData}
            {...defaultGridMemo}
            rowSelection={{
              ...defaultGridMemo.rowSelection,
              headerCheckbox: true,
              checkboxes: params => isEqual(params.data?.demandType, PRODORDER),
            }}
            pagination
            statusBar={statusBar}
            paginationPageSize={100}
            cacheBlockSize={100}
            onGridReady={onGridReady}
            onPaginationChanged={onPaginationChanged}
            onSelectionChanged={onSelectionChanged}
          />
        </Box>
        {openFilter && (
          <ResizableFilterPanel>
            <AdvancedFilter
              columnDefs={columnDefs}
              advancedFilters={advancedFilters}
              onApply={setAdvancedFilters}
              onClose={() => setOpenFilter(false)}
            />
          </ResizableFilterPanel>
        )}
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MpDemandAddModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onConfirm={(data) => {
          saveProdOrder([{...data, location: { id: data.locId }, item: { id: data.itemId }, customer: { id: data.customerId } }]);
        }}
      />
      <MpDemandConstraintModal
        open={constraintModalOpen}
        onClose={() => setConstraintModalOpen(false)}
        order={selectedDemandRow}
        parPlanVerId={mpDemandVersionFilter?.id}
      />
    </>
  );
};

export default withTranslation()(MpDemand);